import { useState } from 'react';
import { Link } from 'react-router-dom';
import personalTrainersData from '@/data/personalTrainers.json';
import type { Coach } from '@/data/types';
import { WHATSAPP_NUMBER } from '@/data/constants';
import { WhatsAppIcon } from '@/components/icons';

const trainers = personalTrainersData as Coach[];

const GOALS = [
  { id: 'lose-weight',       label: 'Lose Weight',          icon: 'ri-scales-3-line' },
  { id: 'build-muscle',      label: 'Build Muscle',          icon: 'ri-boxing-line' },
  { id: 'improve-fitness',   label: 'Improve Fitness',       icon: 'ri-heart-pulse-line' },
  { id: 'sports-performance',label: 'Sports Performance',    icon: 'ri-run-line' },
  { id: 'flexibility',       label: 'Flexibility & Mobility',icon: 'ri-body-scan-line' },
  { id: 'rehabilitation',    label: 'Rehabilitation',        icon: 'ri-first-aid-kit-line' },
];

const STEPS = ['Your Goal', 'Your Info', 'Send'];

export default function PersonalTrainers() {
  const [step, setStep]               = useState(0);
  const [selectedGoal, setSelectedGoal] = useState('');
  const [name, setName]               = useState('');
  const [age, setAge]                 = useState('');
  const [notes, setNotes]             = useState('');
  const [submitted, setSubmitted]     = useState(false);

  const handleSend = () => {
    if (!selectedGoal || !name.trim()) return;
    const goalLabel = GOALS.find((g) => g.id === selectedGoal)?.label ?? selectedGoal;
    const lines = [
      `Hi HUB30! I'd like to be matched with a coach.`,
      ``,
      `*Name:* ${name.trim()}`,
      age.trim() ? `*Age:* ${age.trim()}` : null,
      `*Goal:* ${goalLabel}`,
      notes.trim() ? `*Notes:* ${notes.trim()}` : null,
    ]
      .filter((l) => l !== null)
      .join('\n');

    window.open(`https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(lines)}`, '_blank', 'noopener,noreferrer');
    setSubmitted(true);
  };

  const handleReset = () => {
    setStep(0);
    setSelectedGoal('');
    setName('');
    setAge('');
    setNotes('');
    setSubmitted(false);
  };

  return (
    <section id="coaches" className="py-16 lg:py-32 px-4 lg:px-8 bg-[#111111] border-t border-white/5">
      {/* Header */}
      <div className="max-w-7xl mx-auto mb-12 lg:mb-16 text-center">
        <span data-animate className="inline-flex items-center gap-2 text-[#14B8A6] text-xs tracking-widest font-medium mb-4">
          <span className="w-4 h-px bg-[#14B8A6]" />
          PRIVATE COACHING
          <span className="w-4 h-px bg-[#14B8A6]" />
        </span>
        <h2 data-animate data-delay="1" className="text-3xl sm:text-4xl lg:text-6xl font-bold mb-4 lg:mb-6 text-white">
          Personal Training &amp; PTs
        </h2>
        <p data-animate data-delay="2" className="text-base lg:text-xl text-gray-400 max-w-3xl mx-auto">
          Work one-on-one with our team for private training, strength, plyometrics, and rehab-focused coaching.
        </p>
      </div>

      {/* Trainer Cards */}
      <div className="max-w-4xl mx-auto grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 lg:gap-6">
        {trainers.map((coach, index) => (
          <Link
            key={coach.id}
            to={`/coaches/${coach.slug}`}
            data-animate
            data-delay={(index % 3 + 1) as 1 | 2 | 3}
            className="bg-[#1a1a1a] rounded-3xl overflow-hidden hover:shadow-[0_0_40px_rgba(20,184,166,0.12)] transition-all duration-300 border border-white/[0.08] hover:border-[#14B8A6]/25 group block"
          >
            <div className="relative aspect-[1/1.6] overflow-hidden">
              <img
                src={coach.image}
                alt={coach.name}
                className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
              />
              {/* Gradient overlay at bottom of image */}
              <div className="absolute inset-0 bg-gradient-to-t from-[#1a1a1a] via-transparent to-transparent opacity-60" />
              <div className="absolute top-3 left-3 max-w-[calc(100%-1.5rem)] bg-black/60 backdrop-blur-sm border border-[#14B8A6]/30 text-[#14B8A6] text-xs font-semibold px-3 py-1 rounded-full leading-snug">
                {coach.specialty}
              </div>
            </div>

            <div className="p-5">
              <div className="flex items-start justify-between gap-2 mb-2">
                <h3 className="text-white text-lg font-bold">{coach.name}</h3>
                <span className="text-[#14B8A6] text-xs font-medium opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0 mt-1">
                  View Profile →
                </span>
              </div>
              <div className="mb-3 flex flex-wrap gap-1.5">
                {coach.services.map((service, i) => (
                  <span key={i} className="text-xs text-gray-400 bg-white/[0.05] border border-white/[0.08] px-2.5 py-0.5 rounded-full">
                    {service}
                  </span>
                ))}
              </div>
              <p className="text-gray-500 text-xs leading-relaxed">{coach.description}</p>
            </div>
          </Link>
        ))}
      </div>

      {/* Coach Matching Form */}
      <div className="max-w-2xl mx-auto mt-16 lg:mt-24">
        <div className="text-center mb-10">
          <span className="inline-flex items-center gap-2 text-[#14B8A6] text-xs tracking-widest font-medium mb-4">
            <span className="w-4 h-px bg-[#14B8A6]" />
            GET MATCHED
            <span className="w-4 h-px bg-[#14B8A6]" />
          </span>
          <h3 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-white mb-3">Find Your Coach</h3>
          <p className="text-gray-400 text-sm lg:text-base">
            Tell us your goal and we'll match you with the right coach via WhatsApp.
          </p>
        </div>

        {submitted ? (
          /* Success State */
          <div className="bg-[#1a1a1a] border border-[#14B8A6]/25 rounded-3xl p-8 lg:p-12 text-center shadow-[0_0_60px_rgba(20,184,166,0.07)]">
            <div className="w-16 h-16 bg-[#14B8A6]/10 border border-[#14B8A6]/20 rounded-full flex items-center justify-center mx-auto mb-6">
              <i className="ri-check-line text-[#14B8A6] text-3xl" />
            </div>
            <h4 className="text-white text-xl font-bold mb-2">Message Sent!</h4>
            <p className="text-gray-400 text-sm mb-6 max-w-xs mx-auto leading-relaxed">
              Your WhatsApp message is ready. Our team will reach back shortly to recommend the best coach for you.
            </p>
            <button onClick={handleReset} className="text-[#14B8A6] text-sm font-medium hover:underline">
              Submit another request →
            </button>
          </div>
        ) : (
          <div className="bg-[#171717] border border-white/[0.08] rounded-3xl overflow-hidden shadow-[0_0_60px_rgba(20,184,166,0.06)]">

            {/* Step indicator */}
            <div className="flex border-b border-white/[0.06]">
              {STEPS.map((label, i) => (
                <div
                  key={label}
                  className={`flex-1 py-4 text-center text-xs font-semibold tracking-wider uppercase transition-colors ${
                    i === step
                      ? 'text-[#14B8A6] border-b-2 border-[#14B8A6] bg-[#14B8A6]/5'
                      : i < step
                      ? 'text-gray-500 bg-[#14B8A6]/3'
                      : 'text-gray-600'
                  }`}
                >
                  <span className="hidden sm:inline">{label}</span>
                  <span className="sm:hidden">{i + 1}</span>
                </div>
              ))}
            </div>

            <div className="p-6 lg:p-10">
              {/* Step 0 — Goal */}
              {step === 0 && (
                <div>
                  <p className="text-white text-sm font-semibold mb-4">
                    What's your goal? <span className="text-[#14B8A6]">*</span>
                  </p>
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                    {GOALS.map((goal) => (
                      <button
                        key={goal.id}
                        type="button"
                        onClick={() => setSelectedGoal(goal.id)}
                        className={`flex flex-col items-center gap-2 px-3 py-4 rounded-2xl border text-xs font-medium transition-all duration-200 cursor-pointer ${
                          selectedGoal === goal.id
                            ? 'bg-[#14B8A6]/10 border-[#14B8A6] text-[#14B8A6] shadow-[0_0_16px_rgba(20,184,166,0.15)]'
                            : 'bg-white/[0.03] border-white/10 text-gray-400 hover:border-white/25 hover:text-white hover:bg-white/[0.06]'
                        }`}
                      >
                        <i className={`${goal.icon} text-xl`} />
                        {goal.label}
                      </button>
                    ))}
                  </div>
                  <button
                    type="button"
                    onClick={() => setStep(1)}
                    disabled={!selectedGoal}
                    className="w-full mt-6 bg-[#14B8A6] hover:bg-[#0F9A8A] disabled:bg-white/10 disabled:text-gray-500 disabled:cursor-not-allowed text-white font-semibold py-3.5 rounded-2xl transition-all duration-200"
                  >
                    Next →
                  </button>
                </div>
              )}

              {/* Step 1 — Info */}
              {step === 1 && (
                <div className="space-y-5">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-white text-sm font-semibold mb-2">
                        Your name <span className="text-[#14B8A6]">*</span>
                      </label>
                      <input
                        type="text"
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        placeholder="e.g. Samir Deeb"
                        className="w-full bg-white/[0.05] border border-white/10 rounded-xl px-4 py-3 text-white text-sm placeholder-gray-600 focus:outline-none focus:border-[#14B8A6]/60 focus:bg-white/[0.07] transition-colors"
                      />
                    </div>
                    <div>
                      <label className="block text-white text-sm font-semibold mb-2">Age <span className="text-gray-600 font-normal">(optional)</span></label>
                      <input
                        type="number"
                        value={age}
                        onChange={(e) => setAge(e.target.value)}
                        placeholder="e.g. 28"
                        min={10}
                        max={99}
                        className="w-full bg-white/[0.05] border border-white/10 rounded-xl px-4 py-3 text-white text-sm placeholder-gray-600 focus:outline-none focus:border-[#14B8A6]/60 focus:bg-white/[0.07] transition-colors"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-white text-sm font-semibold mb-2">
                      Notes <span className="text-gray-600 font-normal">(optional)</span>
                    </label>
                    <textarea
                      value={notes}
                      onChange={(e) => setNotes(e.target.value)}
                      placeholder="Injuries, experience level, schedule preferences..."
                      rows={3}
                      className="w-full bg-white/[0.05] border border-white/10 rounded-xl px-4 py-3 text-white text-sm placeholder-gray-600 focus:outline-none focus:border-[#14B8A6]/60 focus:bg-white/[0.07] transition-colors resize-none"
                    />
                  </div>
                  <div className="flex gap-3">
                    <button
                      type="button"
                      onClick={() => setStep(0)}
                      className="flex-1 border border-white/10 text-gray-400 hover:text-white hover:border-white/20 font-semibold py-3.5 rounded-2xl transition-all duration-200"
                    >
                      ← Back
                    </button>
                    <button
                      type="button"
                      onClick={() => setStep(2)}
                      disabled={!name.trim()}
                      className="flex-[2] bg-[#14B8A6] hover:bg-[#0F9A8A] disabled:bg-white/10 disabled:text-gray-500 disabled:cursor-not-allowed text-white font-semibold py-3.5 rounded-2xl transition-all duration-200"
                    >
                      Next →
                    </button>
                  </div>
                </div>
              )}

              {/* Step 2 — Review & Send */}
              {step === 2 && (
                <div className="space-y-5">
                  <div className="bg-white/[0.03] border border-white/[0.08] rounded-2xl p-4 space-y-3 text-sm">
                    <div className="flex justify-between">
                      <span className="text-gray-500">Goal</span>
                      <span className="text-white font-medium">{GOALS.find((g) => g.id === selectedGoal)?.label}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-500">Name</span>
                      <span className="text-white font-medium">{name}</span>
                    </div>
                    {age && (
                      <div className="flex justify-between">
                        <span className="text-gray-500">Age</span>
                        <span className="text-white font-medium">{age}</span>
                      </div>
                    )}
                    {notes && (
                      <div className="flex flex-col gap-1">
                        <span className="text-gray-500">Notes</span>
                        <span className="text-white font-medium text-xs leading-relaxed">{notes}</span>
                      </div>
                    )}
                  </div>

                  <button
                    type="button"
                    onClick={handleSend}
                    className="w-full flex items-center justify-center gap-3 bg-[#25D366] hover:bg-[#1ebe5d] text-white font-semibold py-4 rounded-2xl transition-all duration-200 hover:shadow-[0_0_24px_rgba(37,211,102,0.35)] text-base"
                  >
                    <WhatsAppIcon className="text-xl" />
                    Send via WhatsApp
                  </button>

                  <button
                    type="button"
                    onClick={() => setStep(1)}
                    className="w-full text-gray-500 hover:text-gray-300 text-sm transition-colors"
                  >
                    ← Edit details
                  </button>
                  <p className="text-center text-gray-600 text-xs">
                    Opens WhatsApp with a pre-filled message — no data is stored.
                  </p>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </section>
  );
}
